import React, {useState, useRef, forwardRef} from 'react';
import dayjs from 'dayjs';
import useControllableState from '../hooks/useControllableState';
import useMergedRef from '../hooks/useMergeRef';
import capitalize from '../utils/capitalize';
import RangeCalendar from './RangeCalendar';
import BasePicker from './BasePicker';
import Button from '../Buttons';
import {useConfig} from '../ConfigProvider';
import 'dayjs/locale/es-mx';

const validationRule = val =>
  Array.isArray(val) && val.length === 2 && val.every(v => v instanceof Date);

const isFirstDateSet = val =>
  Array.isArray(val) && val.length === 2 && val[0] instanceof Date;

const DatePickerRange = forwardRef((props, ref) => {
  const {
    diasMaximos = null,
    className,
    clearable,
    clearButton,
    closePickerOnChange,
    dateViewCount,
    dayClassName,
    dayStyle,
    defaultMonth,
    defaultOpen,
    defaultValue,
    defaultView,
    disabled,
    disableDate,
    enableHeaderLabel,
    disableOutOfMonth,
    firstDayOfWeek,
    hideOutOfMonthDates,
    hideWeekdays,
    inputFormat,
    inputPrefix,
    inputSuffix,
    labelFormat,
    seperator,
    locale = 'es-mx',
    maxDate,
    minDate,
    onChange,
    onDropdownClose,
    onDropdownOpen,
    openPickerOnClear,
    renderDay,
    showTodayAction,
    singleDate,
    size,
    style,
    todayActionLabel,
    value,
    weekendDays,
    yearLabelFormat,
    ...rest
  } = props;

  const {locale: themeLocale} = useConfig();

  const finalLocale = locale || themeLocale;

  const dateFormat = inputFormat || 'DD/MM/YYYY';

  const [dropdownOpened, setDropdownOpened] = useState(defaultOpen);

  const inputRef = useRef();

  const [_value, setValue] = useControllableState({
    prop: value,
    defaultProp: defaultValue !== undefined ? defaultValue : [null, null],
    onChange
  });

  const applyRangeChange = range => {
    if (diasMaximos && range[0]) {
      const minDate = dayjs(range[0]).subtract(diasMaximos, 'day').toDate();
      const maxDate = dayjs(range[0]).add(diasMaximos, 'day').toDate();

      if (range[1] && (range[1] < minDate || range[1] > maxDate)) {
        return false;
      }
    }

    setValue(range);

    if (closePickerOnChange && validationRule(range)) {
      setDropdownOpened(false);
      onDropdownClose?.();
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }

    return true;
  };

  const handleValueChange = range => {
    applyRangeChange(range);
  };

  const valueValid = validationRule(_value);
  const firstValueValid = isFirstDateSet(_value);

  const firstDateLabel = _value[0]
    ? capitalize(dayjs(_value[0]).locale(finalLocale).format(dateFormat))
    : '';

  const secondDateLabel = _value[1]
    ? capitalize(dayjs(_value[1]).locale(finalLocale).format(dateFormat))
    : '';

  const handleClear = () => {
    setValue([null, null]);
    setDropdownOpened(true);
    openPickerOnClear && onDropdownOpen?.();
    inputRef.current?.focus();
  };

  const handleDropdownToggle = isOpened => {
    if (!isOpened && firstValueValid && _value[1] === null) {
      handleClear();
    }
    setDropdownOpened(isOpened);
  };

  const today = dayjs().startOf('day').toDate();

  const isTodayDisabled =
    disabled ||
    (minDate instanceof Date && dayjs(today).isBefore(minDate, 'day')) ||
    (maxDate instanceof Date && dayjs(today).isAfter(maxDate, 'day')) ||
    disableDate?.(today);

  const handleSetToday = () => {
    const currentStart = _value[0] instanceof Date ? _value[0] : null;
    const currentEnd = _value[1] instanceof Date ? _value[1] : null;

    if (!currentStart) {
      applyRangeChange([today, null]);
      return;
    }

    if (!currentEnd) {
      applyRangeChange([currentStart, today]);
    }
  };

  return (
    <BasePicker
      dropdownOpened={dropdownOpened}
      setDropdownOpened={handleDropdownToggle}
      ref={useMergedRef(ref, inputRef)}
      size={size}
      style={style}
      className={className}
      inputLabel={
        firstValueValid
          ? `${firstDateLabel} ${seperator} ${secondDateLabel}`
          : ''
      }
      clearable={clearable && firstValueValid}
      clearButton={clearButton}
      onClear={handleClear}
      dateViewCount={dateViewCount}
      onDropdownClose={onDropdownClose}
      onDropdownOpen={onDropdownOpen}
      disabled={disabled}
      inputPrefix={inputPrefix}
      inputSuffix={inputSuffix}
      {...rest}
    >
      <RangeCalendar
        locale={finalLocale}
        defaultMonth={valueValid ? _value[0] : defaultMonth}
        value={_value}
        onChange={handleValueChange}
        labelFormat={labelFormat}
        dayClassName={dayClassName}
        dayStyle={dayStyle}
        disableOutOfMonth={disableOutOfMonth}
        minDate={
          _value[0] && diasMaximos
            ? dayjs(_value[0]).subtract(diasMaximos, 'day').toDate()
            : minDate
        }
        maxDate={
          _value[0] && diasMaximos
            ? dayjs(_value[0]).add(diasMaximos, 'day').toDate()
            : maxDate
        }
        disableDate={disableDate}
        firstDayOfWeek={firstDayOfWeek}
        enableHeaderLabel={enableHeaderLabel}
        singleDate={singleDate}
        dateViewCount={dateViewCount}
        defaultView={defaultView}
        hideOutOfMonthDates={hideOutOfMonthDates}
        hideWeekdays={hideWeekdays}
        renderDay={renderDay}
        weekendDays={weekendDays}
        yearLabelFormat={yearLabelFormat}
      />

      {showTodayAction && (
        <div className="flex justify-end border-t border-gray-200 px-3 py-2">
          <Button
            type="button"
            size="xs"
            variant="default"
            disabled={isTodayDisabled}
            onClick={handleSetToday}
          >
            {todayActionLabel}
          </Button>
        </div>
      )}
    </BasePicker>
  );
});

DatePickerRange.defaultProps = {
  diasMaximos: null,
  closePickerOnChange: true,
  labelFormat: {
    month: 'MMM',
    year: 'YYYY'
  },
  defaultOpen: false,
  seperator: '~',
  clearable: true,
  firstDayOfWeek: 'monday',
  singleDate: false,
  dateViewCount: 1,
  openPickerOnClear: false,
  showTodayAction: false,
  todayActionLabel: 'Día actual'
};

export default DatePickerRange;
